import React from 'react';
import styles from './BrightlightWorkflowDemo.module.css';

const waveformHeights = [
  8, 15, 22, 12, 28, 18, 9, 24, 34, 16, 26, 12, 20, 31, 15, 25, 10, 18, 29, 13, 23, 8,
];
const referrals = [
  ['Acute lumbar pain', 'Urgent'],
  ['Neck pain · 3 mo.', 'Priority'],
  ['Post-op knee', 'Routine'],
];
const clinicalLoads = [
  ['Dr. Chen', 87],
  ['Dr. Lee', 59],
  ['Dr. Roberts', 94],
];

export default function BrightlightWorkflowDemo() {
  return (
    <section className={styles.demo} aria-label="Brightlight AI feature suite">
      <p className={styles.disclaimer}>Illustrative synthetic data</p>

      <div className={styles.heroGrid}>
        <article className={styles.feature}>
          <div className={styles.featureHeader}>
            <p className={`${styles.eyebrow} ${styles.mint}`}>Intelligent triage</p>
            <h4>AI Intake</h4>
            <p>
              Natural-language intake that structures symptoms and relevant clinical context.
            </p>
          </div>

          <div className={styles.intakeWorkspace}>
            <section className={styles.patientCard} aria-label="Synthetic patient response">
              <div className={styles.miniHeader}>
                <span className={`${styles.icon} ${styles.patientIcon}`} aria-hidden="true">
                  P
                </span>
                <span>
                  <strong>Patient response</strong>
                  <small>Martin · illustrative profile</small>
                </span>
              </div>
              <blockquote>
                “I’ve had sharp lower-back pain for two weeks. It’s worse in the morning and
                sometimes travels down my left leg.”
              </blockquote>
              <p className={styles.processing}>
                <span aria-hidden="true">✦</span> AI analysis complete
              </p>
            </section>

            <section className={styles.extractedCard} aria-label="AI extracted intake data">
              <div className={styles.miniHeader}>
                <span className={`${styles.icon} ${styles.aiIcon}`} aria-hidden="true">
                  ✦
                </span>
                <span>
                  <strong>AI extracted data</strong>
                  <small>Reviewed for this example</small>
                </span>
              </div>
              <dl className={styles.facts}>
                <div>
                  <dt>Chief complaint</dt>
                  <dd>Lower back pain</dd>
                </div>
                <div>
                  <dt>Duration</dt>
                  <dd>2 weeks</dd>
                </div>
                <div>
                  <dt>Pain scale</dt>
                  <dd>6/10</dd>
                </div>
                <div>
                  <dt>Pattern</dt>
                  <dd>Worse AM</dd>
                </div>
                <div>
                  <dt>Radiation</dt>
                  <dd>Left leg</dd>
                </div>
                <div>
                  <dt>Aggravating</dt>
                  <dd>Prolonged sitting</dd>
                </div>
              </dl>
              <div className={styles.codes} aria-label="Suggested clinical codes">
                <span>M54.50 · Low back pain</span>
                <span>M54.40 · Lumbar radiculopathy</span>
              </div>
            </section>
          </div>

          <ul className={styles.capabilities} aria-label="AI Intake capabilities">
            <li>90% faster intake</li>
            <li>Multi-language support</li>
            <li>Pre-populated charts</li>
            <li>Urgency detection</li>
            <li>Insurance pre-check</li>
          </ul>
        </article>

        <article className={`${styles.feature} ${styles.scribe}`}>
          <div className={styles.featureHeader}>
            <p className={`${styles.eyebrow} ${styles.purple}`}>Real-time documentation</p>
            <h4>AI Scribe</h4>
            <p>Captures the conversation and structures clinical notes while the visit unfolds.</p>
          </div>

          <div className={styles.recording}>
            <div className={styles.recordingHeader}>
              <div>
                <span className={styles.liveDot} aria-hidden="true" />
                <span>
                  <strong>Recording session</strong>
                  <small>Emma Wilson · PT</small>
                </span>
              </div>
              <div className={styles.timer}>
                <strong>24:35</strong>
                <small>Live</small>
              </div>
            </div>

            <div className={styles.waveform} aria-label="Audio waveform visualization">
              {waveformHeights.map((height, index) => (
                <span key={`${height}-${index}`} style={{ height: `${height}px` }} />
              ))}
            </div>

            <div className={styles.transcript}>
              <span>Live transcription</span>
              <p>
                “Range of motion has improved significantly. She reports pain decreased from a
                seven to a four.”
              </p>
            </div>

            <dl className={styles.soapRows}>
              <div>
                <dt>S</dt>
                <dd>Pain decreased 7→4; improved function.</dd>
              </div>
              <div>
                <dt>O</dt>
                <dd>ROM: shoulder flexion 160° (+15°).</dd>
              </div>
              <div>
                <dt>A</dt>
                <dd>Progressing well. Continue protocol.</dd>
              </div>
              <div>
                <dt>P</dt>
                <dd>Add HEP; follow-up in one week.</dd>
              </div>
            </dl>
          </div>

          <ul className={styles.capabilities} aria-label="AI Scribe capabilities">
            <li>98% accurate</li>
            <li>Auto SOAP</li>
            <li>EMR-ready</li>
            <li>Review &amp; edit</li>
          </ul>
        </article>
      </div>

      <div className={styles.supportGrid}>
        <article className={styles.supportCard}>
          <div>
            <p className={`${styles.eyebrow} ${styles.mint}`}>Routing</p>
            <h4>Smart Referral Triage</h4>
            <p>Auto-prioritize and route referrals to the right provider.</p>
          </div>
          <ul className={styles.referrals}>
            {referrals.map(([title, urgency]) => (
              <li key={title}>
                <span>
                  <i className={styles[urgency.toLowerCase()]} aria-hidden="true" />
                  {title}
                </span>
                <strong className={styles[urgency.toLowerCase()]}>{urgency}</strong>
              </li>
            ))}
          </ul>
        </article>

        <article className={styles.supportCard}>
          <div>
            <p className={`${styles.eyebrow} ${styles.purple}`}>Optimization</p>
            <h4>Clinical Load Management</h4>
            <p>Balance workloads and prevent provider burnout.</p>
          </div>
          <div className={styles.loadRows}>
            {clinicalLoads.map(([name, load]) => (
              <div key={name}>
                <span>{name}</span>
                <progress aria-label={`${name} clinical load`} max="100" value={load}>
                  {load}%
                </progress>
                <span>{load}%</span>
              </div>
            ))}
          </div>
          <p className={styles.suggestion}>
            <span aria-hidden="true">✦</span> <span>Redistribute 2 appointments</span>
          </p>
        </article>

        <article className={styles.supportCard}>
          <div>
            <p className={`${styles.eyebrow} ${styles.yellow}`}>Automation</p>
            <h4>Smart Actions</h4>
            <p>Automated workflow triggers at the right time.</p>
          </div>
          <dl className={styles.metrics}>
            <div>
              <dt>47</dt>
              <dd>Tasks created</dd>
            </div>
            <div>
              <dt>12</dt>
              <dd>Forms sent</dd>
            </div>
            <div>
              <dt>8</dt>
              <dd>Calls queued</dd>
            </div>
            <div>
              <dt>23</dt>
              <dd>Notes filed</dd>
            </div>
          </dl>
        </article>

        <article className={styles.supportCard}>
          <div>
            <p className={`${styles.eyebrow} ${styles.cyan}`}>Discovery</p>
            <h4>Semantic Search</h4>
            <p>Find similar cases and providers with natural language.</p>
          </div>
          <div className={styles.search}>
            <span aria-hidden="true">⌕</span>
            <span>similar lumbar cases</span>
            <kbd>⌘ K</kbd>
          </div>
          <ul className={styles.people}>
            <li>
              <span className={styles.avatar}>MB</span>
              <span>
                <strong>Michael B.</strong>
                <small>92% match</small>
              </span>
            </li>
            <li>
              <span className={styles.avatar}>MK</span>
              <span>
                <strong>MK clinic</strong>
                <small>Provider</small>
              </span>
            </li>
          </ul>
        </article>
      </div>
    </section>
  );
}
